import * as React from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Snackbar from '@mui/material/Snackbar';
import useMediaQuery from '@mui/material/useMediaQuery';

import dayjs from 'dayjs';

import Confirm from '../../components/Confirm';
import Edit from '../../components/Edit';
import {
    ExportPreparingDialog,
    RecordsExportDialog,
    RecordsTable,
    filterRecordRows,
    useDeleteRecords,
    useRecordExport,
    useRecordsQuery,
} from '../../features/records';
import '../../features/records/components/Records.css';
import {
    InfiniteScrollList,
    RecordFilterDialog,
    ScrollTopButton,
    TablePagination,
    useRecordFilters,
    useRecordTable,
} from '../../features/shared';
import { temperatureContext } from '../../lib/context';
import RecordsToolbarSection from './RecordsToolbarSection';

function Records() {
    const tempUnit = useContext(temperatureContext);
    const { t } = useTranslation(undefined, { keyPrefix: 'Records' });
    const { t: tNotify } = useTranslation(undefined, { keyPrefix: 'Notification' });
    const isMobile = useMediaQuery('(max-width: 768px)');
    const translation = React.useMemo(() => ({ D: t('D'), H: t('H'), M: t('M') }), [t]);

    const filters = useRecordFilters({ initialStart: () => dayjs().subtract(1, 'month') });
    const { data: rows, isPending } = useRecordsQuery(filters.range, { tempUnit, translation });
    const records = React.useMemo(() => filterRecordRows(rows ?? [], filters.applied), [rows, filters.applied]);
    const table = useRecordTable(records);
    const deleteRecords = useDeleteRecords();

    const [isFilterOpen, setIsFilterOpen] = React.useState(false);
    const [openConfig, setOpenConfig] = React.useState(false);
    const [openConfirm, setOpenConfirm] = React.useState(false);
    const [exportDialogOpen, setExportDialogOpen] = React.useState(false);
    const [saveAlert, setSaveAlert] = React.useState(false);
    const [alertState, setAlertState] = React.useState({ severity: 'error', message: '' });
    const showAlert = (severity: string, message: string) => {
        setAlertState({ severity, message });
        setSaveAlert(true);
    };
    const closeAlert = (_event?: unknown, reason?: string) => {
        if (reason !== 'clickaway') setSaveAlert(false);
    };
    const { preparing, setPreparing, exportRecords } = useRecordExport(showAlert);

    // Edit, export and delete act on the selection; with nothing selected, say so instead.
    const withSelection = (message: string, action: () => void) => () => {
        if (table.selected.length === 0) showAlert('warning', message);
        else action();
    };

    const handleConfirmDelete = () => {
        deleteRecords.mutate(
            table.selectedRows.map((row) => `${row.deviceId} ${row.index}`),
            {
                onSuccess: () => showAlert('success', tNotify('deleteSuccess')),
                onError: () => showAlert('error', tNotify('deleteFailed')),
            }
        );
        setOpenConfirm(false);
    };

    const tableProps = {
        tKey: 'Records',
        selected: table.selected,
        onSelectAll: table.toggleAll,
        isSelected: table.isSelected,
        onRowClick: table.toggleRow,
        onDelete: withSelection(t('selectDeleteMessage'), () => setOpenConfirm(true)),
        isMobile,
        mobileDrawerVariant: 'records',
        selectedDeviceId: table.selectedDeviceIds,
        selectedIndex: table.selectedIndexes,
        ro: true,
        onRowEdit: (row: any) => {
            table.selectOnly(row);
            setOpenConfig(true);
        },
        loading: isPending,
    };

    return (
        <div className='RecordsPage mt-2 min-h-screen w-full px-4 pb-6 md:mt-8 md:px-6 lg:px-8'>
            <Snackbar open={saveAlert} autoHideDuration={6000} onClose={closeAlert}>
                <Alert onClose={closeAlert} severity={alertState.severity as any}>
                    {alertState.message}
                </Alert>
            </Snackbar>
            <RecordsToolbarSection
                isMobile={isMobile}
                searchString={filters.searchString}
                setSearchString={filters.setSearchString}
                onSearch={() => {
                    filters.search();
                    table.setPage(1);
                }}
                onOpenExport={withSelection(t('selectExportMessage'), () => setExportDialogOpen(true))}
                onOpenEdit={withSelection(t('selectEditMessage'), () => setOpenConfig(true))}
                onOpenFilter={() => setIsFilterOpen(true)}
            />
            <RecordFilterDialog
                open={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                filters={filters}
                onChanged={() => table.setPage(1)}
                tKey='Records.Filter'
            />
            <div className='mt-4'>
                <InfiniteScrollList
                    enabled={isMobile}
                    loaded={table.visibleRecords.length}
                    total={records.length}
                    onLoadMore={table.loadMore}
                >
                    <RecordsTable
                        {...tableProps}
                        paginatedRecords={isMobile ? table.visibleRecords : table.paginatedRecords}
                    />
                </InfiniteScrollList>
            </div>
            {!isMobile && <TablePagination page={table.page} pageCount={table.pageCount} onChange={table.changePage} />}
            <ScrollTopButton enabled={isMobile} />

            <RecordsExportDialog
                open={exportDialogOpen}
                count={table.selected.length}
                onClose={() => setExportDialogOpen(false)}
                onChoose={(choice) => {
                    setExportDialogOpen(false);
                    exportRecords(table.selectedRows, choice);
                }}
            />
            <ExportPreparingDialog open={preparing} onClose={() => setPreparing(false)} />
            <Edit
                openConfig={openConfig}
                setOpenConfig={setOpenConfig}
                setSaveAlert={setSaveAlert}
                setAlertState={setAlertState}
                selectedDeviceId={table.selectedDeviceIds}
                selectedIndex={table.selectedIndexes}
                selectedRecords={table.selectedRows}
                tKey='Records.Edit'
                ro={true}
            />
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogContent>
                    <Confirm
                        openConfirm={openConfirm}
                        setOpenConfirm={setOpenConfirm}
                        records={table.selectedRows.map((row) => `${row.deviceId} ${row.startTime} ${row.endTime}`)}
                        handleConfirm={handleConfirmDelete}
                        tKey='Records.Confirm'
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Records;
